import React, { useState, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { ProjectTagType } from '@/types';
import { findCategory } from '@/utils/categories';
import { OrdersFilterOptions } from '.';
import { useAsyncState } from '@/utils/hooks';
import { getOrderFilterData } from '@/services/orders';
import { TagInput } from '@/components/TagInput';
import UnitDropdown from '@/seller/components/UnitDropdown';
import { FilterDate } from './FilterDate';
import { Trans, useLingui } from '@lingui/react/macro';
import { useLingui as useLinguiNoMacro } from '@lingui/react';
import {
    FilterButtons,
    FilterDropDown,
    FilterPopOut,
    useIsFilterModified,
} from '@ygtechnologies/bygr-frontend-component-library';

interface OrderFilterProps {
    onFilterChange: (f: OrdersFilterOptions) => void;
    filter: OrdersFilterOptions;
    onClear: () => void;
    projectId: number;
    isLoading: boolean;
    disableStatus?: boolean;
}

const OrderFilters: React.FC<OrderFilterProps> = ({
    filter,
    onClear,
    onFilterChange,
    projectId,
    isLoading,
    disableStatus,
}) => {
    const { _ } = useLinguiNoMacro();
    const { t } = useLingui();

    const [isOpen, setIsOpen] = useState(false);
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const [filterData] = useAsyncState(() => getOrderFilterData(projectId), [projectId], null);
    const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
    const filterModified = useIsFilterModified(filter, OrdersFilterOptions.empty);

    const categoryOptions = useMemo(() => {
        return filterData?.categoryIds.map((id) => {
            const result = findCategory(itemCategories, id);
            const path = result.path.reverse();
            return { name: path.map((p) => p.name).join(' > '), id: id };
        });
    }, [filterData, itemCategories]);

    const getStatusDropdown = useMemo(() => {
        if (!filterData) return null;
        return filterData!.statuses.map((s) => ({
            id: s,
            name: _({ id: `OrderStatus.${s}` }),
        }));
    }, [filterData]);

    const handleFilterChange = (
        newValue: { value: string | number; label: ReactNode } | number[],
        keyName: keyof OrdersFilterOptions
    ) => {
        if (!newValue || !Array.isArray(newValue)) {
            onFilterChange({ ...filter, [keyName]: [] });
        } else {
            onFilterChange({ ...filter, [keyName]: newValue });
        }
    };
    return (
        <>
            <FilterButtons
                searchText={filter.searchText}
                onSearchTextChange={(v) => onFilterChange({ ...filter, searchText: v })}
                isLoading={isLoading}
                onButtonClick={() => setIsOpen((v) => !v)}
                isActive={filterModified}
            />

            {filterData && (
                <FilterPopOut onClear={onClear} onClose={() => setIsOpen(false)} isVisible={isOpen}>
                    <label className="filter-label">
                        <Trans>Units</Trans>
                    </label>

                    <UnitDropdown
                        onChange={(unitIds) => handleFilterChange(unitIds, 'units')}
                        units={filterData.units}
                        value={filter.units}
                    />
                    {!disableStatus && (
                        <FilterDropDown
                            data={getStatusDropdown!}
                            label={<Trans>Internal statuses</Trans>}
                            placeholder={t`Any status`}
                            value={filter.statuses}
                            onChange={(v) =>
                                handleFilterChange(
                                    v.map((e) => e.id),
                                    'statuses'
                                )
                            }
                        />
                    )}

                    <FilterDropDown
                        data={filterData.residentialUnitGroups}
                        label={<Trans>Unit groups</Trans>}
                        placeholder={t`Any unit group`}
                        value={filter.residentialUnitGroupIds}
                        onChange={(v) =>
                            handleFilterChange(
                                v.map((e) => e.id),
                                'residentialUnitGroupIds'
                            )
                        }
                    />
                    <FilterDropDown
                        data={filterData.owners}
                        label={<Trans>Owners</Trans>}
                        placeholder={t`Any owner`}
                        value={filter.owners}
                        onChange={(v) =>
                            handleFilterChange(
                                v.map((e) => e.id),
                                'owners'
                            )
                        }
                    />
                    {/* <FilterDropDown
                        data={orderTypeOptions}
                        label={<Trans>Order types</Trans>}
                        placeholder={t`Any order type`}
                        value={filter.orderTypes}
                        onChange={(v) =>
                            handleFilterChange(
                                v.map((e) => e.id),
                                'orderTypes'
                            )
                        }
                    /> */}
                    <FilterDropDown
                        data={categoryOptions!}
                        label={<Trans>Categories</Trans>}
                        placeholder={t`Any category`}
                        value={filter.itemCategories}
                        onChange={(v) =>
                            handleFilterChange(
                                v.map((e) => e.id),
                                'itemCategories'
                            )
                        }
                    />

                    <FilterDropDown
                        data={filterData.deadlines}
                        label={<Trans>Deadlines</Trans>}
                        placeholder={t`Any deadline`}
                        value={filter.projectDeadlineIds}
                        onChange={(v) =>
                            handleFilterChange(
                                v.map((e) => e.id),
                                'projectDeadlineIds'
                            )
                        }
                    />
                    <label className="filter-label">
                        <Trans>Tags</Trans>
                    </label>
                    <TagInput
                        value={filter.tags}
                        placeholder={t`Tags`}
                        onTagsChange={(tags) =>
                            handleFilterChange(
                                tags.map((t) => t.id),
                                'tags'
                            )
                        }
                        type={ProjectTagType.Room}
                        projectId={projectId}
                    />

                    <label className="filter-label">
                        <Trans>Date</Trans>
                    </label>
                    <FilterDate
                        onChange={(startDate, endDate) =>
                            onFilterChange({
                                ...filter,
                                orderDateFrom: startDate?.toISOString() ?? null,
                                orderDateTo: endDate?.toISOString() ?? null,
                            })
                        }
                        open={isDateFilterOpen}
                        setOpen={setIsDateFilterOpen}
                        startDate={filter.orderDateFrom}
                        endDate={filter.orderDateTo}
                    />
                </FilterPopOut>
            )}
        </>
    );
};

export default OrderFilters;
